import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminStats } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Image as ImageIcon, Inbox, ArrowUpRight, UploadCloud, Users as UsersIcon, Plane, Camera } from "lucide-react";

const CATEGORY_ORDER = [
  "Drone", "Roadworks", "Structures", "People", "Night Works",
  "Depots", "Plant", "Traffic Management", "Events", "General",
];

const orderedCats = (byCategory = {}) => {
  const extras = Object.keys(byCategory).filter((c) => !CATEGORY_ORDER.includes(c)).sort();
  return [...CATEGORY_ORDER, ...extras].map((c) => [c, byCategory[c] || 0]);
};

const ShotTypeCard = ({ icon: Icon, title, data, testid }) => (
  <div data-testid={testid} className="bg-[#0f0f0f] border border-[#1a1a1a] p-6">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#7BA8CC]">
      <Icon size={13} /> {title}
    </div>
    <div className="font-display text-4xl tracking-tight mt-2">{data?.total ?? "–"}</div>
    <div className="flex gap-5 mt-3 text-[12px] text-[#A3A3A3]">
      <span>Photos <span className="text-white">{data?.photos ?? "–"}</span></span>
      <span>Videos <span className="text-white">{data?.videos ?? "–"}</span></span>
    </div>
  </div>
);

const CategoryList = ({ title, total, rows, testid }) => (
  <div data-testid={testid} className="bg-[#0f0f0f] border border-[#1a1a1a] p-6">
    <div className="flex items-baseline justify-between">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">{title}</div>
      <div className="font-display text-2xl tracking-tight">{total ?? "–"}</div>
    </div>
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
      {rows.map(([cat, count]) => (
        <div key={cat} className="flex items-center justify-between text-[13px] border-b border-[#161616] pb-1.5">
          <span className={count === 0 ? "text-[#525252]" : "text-[#c9d1d9]"}>{cat}</span>
          <span className={count === 0 ? "text-[#525252]" : "text-white"}>{count}</span>
        </div>
      ))}
    </div>
  </div>
);

const Stat = ({ label, value, hint, testid }) => (
  <div
    data-testid={testid}
    className="bg-[#0f0f0f] border border-[#1a1a1a] p-6 flex flex-col gap-2"
  >
    <div className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">{label}</div>
    <div className="font-display text-4xl tracking-tight">{value}</div>
    {hint && <div className="text-[12px] text-[#A3A3A3]">{hint}</div>}
  </div>
);

const QuickLink = ({ to, icon: Icon, title, body, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group bg-[#0f0f0f] border border-[#1a1a1a] hover:border-[#4A6D8C] transition-colors p-6 flex items-start gap-4"
  >
    <div className="w-10 h-10 rounded-full bg-[#4A6D8C]/15 flex items-center justify-center text-[#658CAE]">
      <Icon size={18} />
    </div>
    <div className="flex-1">
      <div className="font-display text-lg tracking-tight">{title}</div>
      <div className="text-[13px] text-[#A3A3A3] mt-1">{body}</div>
    </div>
    <ArrowUpRight size={16} className="text-[#525252] group-hover:text-white" />
  </Link>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    adminStats().then(setStats).catch(() => {});
  }, []);

  return (
    <div className="px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">
            Overview
          </div>
          <h1 className="font-display text-4xl tracking-tight">Studio dashboard</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/admin/gallery/bulk" data-testid="dashboard-bulk-upload" className="hl-btn-ghost inline-flex items-center gap-2">
            <UploadCloud size={14} /> Bulk upload
          </Link>
          <Link to="/admin/gallery/new" data-testid="dashboard-new-upload" className="hl-btn-primary">
            Upload image
          </Link>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Gallery published" value={stats?.gallery_published ?? "–"} hint={`${stats?.gallery_drafts ?? 0} drafts pending`} testid="stat-published" />
        <Stat label="Photos" value={stats?.photos ?? "–"} testid="stat-photos" />
        <Stat label="Videos" value={stats?.videos ?? "–"} testid="stat-videos" />
        <Stat label="Briefs" value={stats?.contacts_total ?? "–"} hint={`${stats?.contacts_unread ?? 0} unread`} testid="stat-contacts" />
        {user?.role === "admin" && <Stat label="Users" value={stats?.users ?? "–"} testid="stat-users" />}
      </div>

      <div className="mt-12">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-4">
          Live content breakdown <span className="text-[#525252] normal-case tracking-normal">· published items</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="content-breakdown">
          <ShotTypeCard icon={Plane} title="Aerial & Drone" data={stats?.content?.aerial} testid="breakdown-aerial" />
          <ShotTypeCard icon={Camera} title="Ground & On-Site" data={stats?.content?.ground} testid="breakdown-ground" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <CategoryList
            title="Photography"
            total={stats?.content?.photography?.total}
            rows={orderedCats(stats?.content?.photography?.by_category)}
            testid="breakdown-photography"
          />
          <CategoryList
            title="Video clips"
            total={stats?.content?.video_clips?.total}
            rows={orderedCats(stats?.content?.video_clips?.by_category)}
            testid="breakdown-video-clips"
          />
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <QuickLink to="/admin/gallery" icon={ImageIcon} title="Manage gallery" body="Review drafts, edit captions, reorder featured tiles, delete old work." testid="dashboard-manage-gallery" />
        <QuickLink to="/admin/inbox" icon={Inbox} title="Briefs inbox" body="Read incoming enquiries from the Contact form, mark as read, reply by email." testid="dashboard-open-inbox" />
        {user?.role === "admin" && (
          <QuickLink to="/admin/users" icon={UsersIcon} title="Users & roles" body="Add editors or other admins to your studio." testid="dashboard-manage-users" />
        )}
      </div>
    </div>
  );
}
